import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../../components/Pagination";

const HtmlDocZipList = () => {
  const [data, setData] = useState([]);
  const [htmlDocId, setHtmlDocId] = useState([]);
  const [campaignId, setCampaignId] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);
  const [activeStatus, setActiveStatus] = useState("");
  const [message, setMessage] = useState("");
  const [publishInfo, setPublishInfo] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  const fetchHtmlDocData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/Search',
        {
          id: 0,
          applycreateddate: false,
          fromcreateddate: from,
          endcreateddate: to,
          active: activeStatus,
          name: "",
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setHasMore(response.data.data.length === dataLimit);
      } else {
        setData([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching HTML document data:", error);
      setData([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchHtmlDocDataForId = async () => {
    try {
      const response = await axios.get('https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/GetDistinct/name', {
        headers: {
          accept: "text/plain",
          "X-Authorization": token,
          UserId: userid,
        },
      });

      if (response.data && Array.isArray(response.data.data)) {
        setHtmlDocId(response.data.data);
      } else {
        setHtmlDocId([]);
      }
    } catch (error) {
      console.error("Error fetching HTML document IDs:", error);
    }
  };

  useEffect(() => {
    fetchHtmlDocData();
    fetchHtmlDocDataForId();
  }, [getUrlByOperation, token, userid, dataLimit, count, from, to, activeStatus]);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0); // Reset to first page when changing items per page
  };

  // Pagination navigation functions
  const goFirst = () => {
    if (count !== 0) setCount(0);
  };
  
  const goPrev = () => {
    if (count > 0) setCount(count - 1);
  };
  
  const goNext = () => {
    if (hasMore) setCount(count + 1);
  };

  const handlePageChange = (page) => {
    setCount(page - 1); // Convert 1-based page to 0-based index
  };

  const handleAddPop = () => {
    navigate("/addhtmldoczip");
  };

  const handleUpdatePop = (id) => {
    navigate(`/updatehtmldoczip/${id}`);
  };

  const handleShowPop = (id, name) => {
    navigate(`/showhtmldoc/${id}/${name}`);
  };

  const handleDelete = async (id) => {
    try {
      await axios.post('https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/Delete', { id }, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      });
      setMessage("Data deleted successfully!");
      fetchHtmlDocData(); // Refresh data after delete
    } catch (error) {
      console.error("Error deleting item:", error);
      setMessage("Failed to delete data");
    }
  };

  const handleToggle = async (item) => {
    const apiUrl = item.active 
      ? 'https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/UpdateStatus/Deactive'
      : 'https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/UpdateStatus/Active';

    try {
      await axios.post(apiUrl, { id: item.id }, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      });
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
    } catch (error) {
      console.error("Error updating status:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
        setCopySuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

   
const handleCopyClick = async (id, publishId) => {
  const publishUrl = `${window.location.origin}/SMD/htmldoc/${publishId}`;
  
  try {
    await copyToClipboard(publishUrl);
    setMessage("URL copied to clipboard!");
    console.log("URL copied to clipboard:", publishUrl);
    setCopySuccess(true);
  } catch (error) {
    console.error("Failed to copy URL:", error);
    setMessage("Failed to copy URL to clipboard");
  }
};


// Updated handlePublish function with fixed clipboard handling
const handlePublish = async (id) => {
  setPublishLoading(true);
  try {
    // First, call the API to generate the publish ID
    const response = await axios.post(
      'https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/Generate/PublishId',
      { id },
      {
        headers: {
          'accept': 'text/plain',
          'UserId': userid || '1',
          'X-Authorization': token,
          'Content-Type': 'application/json-patch+json'
        }
      }
    );

    // Check if the API response is successful
    if (response.data && response.data.isresponse) {
      // Get the publish ID from the response
      const publishId = response.data.publishid || "";
      
      // Generate the publishable URL
      const publishUrl = `${window.location.origin}/SMD/htmldoc/${publishId}`;
      
      // Update state with the publish info
      setPublishInfo({ 
        id, 
        encodedUrl: publishUrl, 
        encodedId: publishId 
      });
      
      // Copy URL to clipboard using your fallback function
      try {
        await copyToClipboard(publishUrl);
        setMessage("URL copied to clipboard!");
        setCopySuccess(true);
      } catch (copyError) {
        console.error("Failed to copy URL:", copyError);
        setMessage("Publish successful, but failed to copy URL to clipboard");
      }
      
      // Refresh the data to get the updated publishid
      fetchHtmlDocData();
    } else {
      // Handle API error
      throw new Error(response.data?.errormsg || "Failed to generate publish ID");
    }
  } catch (error) {
    console.error("Error generating publish ID:", error);
    setMessage(error.message || "Failed to generate publish URL");
  } finally {
    setPublishLoading(false);
  }
};

// Ensure your copyToClipboard function is properly defined 
function copyToClipboard(text) {
  // Try to use the Clipboard API first
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(err => {
        console.error('Could not copy text using Clipboard API: ', err);
        return fallbackCopyToClipboard(text);
      });
  } else {
    // Fall back to the textarea method
    return Promise.resolve(fallbackCopyToClipboard(text));
  }
}

function fallbackCopyToClipboard(text) {
  // Create a temporary textarea element
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Make the textarea out of viewport
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  
  // Select and copy
  textArea.focus();
  textArea.select();
  
  let success = false;
  try {
    success = document.execCommand('copy');
  } catch (err) {
    console.error('Failed to copy with fallback method:', err);
  }
  
  // Remove the textarea from DOM
  document.body.removeChild(textArea);
  return success;
}

  const handleUnPublish = async (id, publishId) => {
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/UnPublishId',
        { id },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid || '1',
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );
      
      if (response.data && response.data.isresponse) {
        setMessage("HTML Document unpublished successfully!");
        fetchHtmlDocData();
      } else {
        throw new Error(response.data?.errormsg || "Failed to unpublish HTML document");
      }
    } catch (error) {
      console.error("Error unpublishing HTML document:", error);
      setMessage(error.message || "Failed to unpublish HTML document");
    }
  };

  const handleDownload = async (id, name) => {
    try {
      // First, get the download path from the Search API
      const searchResponse = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/Search',
        {
          id: id,
          applycreateddate: false,
          fromcreateddate: 0,
          endcreateddate: 0,
          active: "",
          name: "",
          ispageload: false,
          pagesize: 1,
          currentpageno: 0,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      // Check if we got the data and download path
      if (searchResponse.data && searchResponse.data.data && searchResponse.data.data.length > 0) {
        const item = searchResponse.data.data[0];
        const downloadPath = item.downloadpath; // Assuming the key is 'downloadpath'
        
        if (!downloadPath) {
          throw new Error("Download path not found in the response");
        }

        // Now download the file using the download path
        const response = await axios.get(
          downloadPath,
          {
            headers: {
              'X-Authorization': token,
              'UserId': userid,
            },
            responseType: 'blob'
          }
        );

        // Create a blob from the response
        const blob = new Blob([response.data], { type: 'application/zip' });
        
        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name || 'document'}.zip`;
        
        // Trigger the download
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // setMessage("ZIP file downloaded successfully!");
      } else {
        throw new Error("No data found for the specified ID");
      }
    } catch (error) {
      console.error("Error downloading ZIP file:", error);
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
      }
      setMessage("Failed to download ZIP file");
    }
  };

  const renderPublishColumn = (item) => {
    // If the item already has a publishid, show it with a copy button
    if (item.publishid) {
      // Truncate publishid if it's longer than 5 characters
      const displayId = item.publishid.length > 5 
        ? `${item.publishid.substring(0, 5)}...` 
        : item.publishid;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <div className="btn-group">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleCopyClick(item.id, item.publishid)}
              title="Copy URL"
            >
              <i className="fas fa-copy mr-1"></i> URL
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleUnPublish(item.id, item.publishid)}
              title="Unpublish"
            >
              <i className="fas fa-eye-slash"></i>
            </button>
          </div>
        </div>
      );
    }
    
    // Check if this item is currently being processed
    if (publishInfo.id === item.id) {
      // Truncate encodedId if it's longer than 5 characters
      const displayId = publishInfo.encodedId.length > 5 
        ? `${publishInfo.encodedId.substring(0, 5)}...` 
        : publishInfo.encodedId;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleCopyClick(item.id, publishInfo.encodedId)}
            title="Copy URL"
          >
            <i className="fas fa-copy mr-1"></i> Copy URL
          </button>
        </div>
      );
    }
    
    // If no publishid and not currently processing, show the publish button
    return (
      <button
        className="btn btn-sm btn-success"
        onClick={() => handlePublish(item.id)}
        disabled={publishLoading}
      >
        {publishLoading ? (
          <>
            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
            Publishing...
          </>
        ) : (
          <>
            <i className="fas fa-globe mr-1"></i> Publish
          </>
        )}
      </button>
    );
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">HTML Documents</h1>
                <button type="button" className="btn btn-success" onClick={handleAddPop}>
                  Add HTML Document
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                  <table className="table table-hover table-striped">
  <thead className="thead-light">
    <tr>
      <th style={{width: '5%'}} className="text-center">Id</th>
      <th style={{width: '15%'}}>Name</th>
      <th style={{width: '15%'}}>HTML Document Description</th>
      <th style={{width: '25%'}} className="text-center">Action</th>
      <th style={{width: '10%'}} className="text-center">Active</th>
      <th style={{width: '15%'}} className="text-center">Publish</th>
    </tr>
  </thead>
  <tbody>
    {data.map((item, index) => (
      <tr key={index}>
        <td className="text-center">{item.id}</td>
        <td>
          <strong>{item.name}</strong>
        </td>
        <td>{item.remark || "-"}</td>
        <td className="text-center">
          <div className="btn-group">
            <button
              type="button"
              className="btn btn-sm btn-primary mr-1"
              onClick={() => handleUpdatePop(item.id)}
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-info mr-1"
              onClick={() => handleDownload(item.id, item.name)}
              title="Download ZIP"
            >
              <i className="fas fa-download"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(item.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </td>
        <td className="text-center">
          <Switch
            isOn={item.active}
            handleToggle={() => handleToggle(item)}
            id={item.id}
          />
        </td>
        <td className="text-center">
          {renderPublishColumn(item)}
        </td>
      </tr>
    ))}
  </tbody>
</table>
                    </div>
                    
                    {/* Pagination */}
                    <Pagination
                      currentPage={count + 1}
                      totalPages={hasMore ? count + 2 : count + 1}
                      onPageChange={handlePageChange}
                      itemsPerPage={dataLimit}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      totalItems={data.length + (hasMore ? dataLimit : 0)}
                      showPageNumbers={false}
                      showItemsPerPageSelector={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display - show both as toast notification AND component */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "10px", width: "25%", zIndex: "9999" }}>
          <div
            className={`alert ${copySuccess || message === "Data deleted successfully!" ? "alert-success" : "alert-danger"} d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>{message}</div>
          </div>
        </div>
      )}
      
      {message === "URL copied to clipboard!" && <SuccessMessage message={message} />}
      {message === "Data deleted successfully!" && <SuccessMessage message={message} />}
      {message === "Failed to delete data" && <FailedMessage message={message} />}
    </>
  );
};

export default HtmlDocZipList;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}